import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Navbar from '../components/Header/Navbar';
import Footer from '../components/Footer';
import Button from '../components/Button';
import ReelPlayer from '../components/reels/ReelPlayer';
import { useAuth } from '../contexts/AuthContext';
import { useSavedReels } from '../hooks/useSavedReels';
import { useSaveReel } from '../hooks/useSaveReel';
import type { ReelViewModel } from '../api/types/reel';

const KaydedilenGonderiler: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { reels, loading, error, refetch, removeReel } = useSavedReels(
    isAuthenticated && Boolean(user?.id)
  );
  const { toggleSave, loading: saveLoading } = useSaveReel();
  const [activeReelId, setActiveReelId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/giris', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleUnsave = useCallback(
    async (reel: ReelViewModel) => {
      const ok = await toggleSave(reel.id, true);
      if (ok) {
        removeReel(reel.id);
        if (activeReelId === reel.id) setActiveReelId(null);
      }
    },
    [toggleSave, removeReel, activeReelId]
  );

  if (!isAuthenticated) return null;

  const activeReel = reels.find((reel) => reel.id === activeReelId) ?? null;

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
      <Navbar />

      <main className="container py-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <h1 className="mb-0">Kaydedilen Gönderiler</h1>
          <Link className="btn btn-outline-primary btn-sm" to="/gonderiler">
            <i className="fas fa-play me-2" />
            Gönderilere Dön
          </Link>
        </div>

        {error && (
          <div className="alert alert-warning mb-3 d-flex flex-wrap align-items-center gap-2" role="alert">
            <span>{error}</span>
            <Button className="btn btn-sm btn-outline-primary" onClick={() => void refetch()}>
              Tekrar Dene
            </Button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5 text-muted">Yükleniyor...</div>
        ) : !error && reels.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-bookmark fa-3x text-muted mb-3" />
            <h3>Henüz kaydedilmiş gönderi yok</h3>
            <p className="text-muted">Gönderiler akışından beğendiğiniz reelleri kaydedebilirsiniz.</p>
            <Link className="btn btn-primary" to="/gonderiler">
              Gönderilere Git
            </Link>
          </div>
        ) : (
          <div className="row g-4">
            {reels.map((reel) => (
              <div key={reel.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                <div className="card border-0 shadow-sm h-100">
                  <button
                    type="button"
                    className="p-0 border-0 bg-transparent w-100"
                    onClick={() => setActiveReelId(reel.id)}
                    aria-label="Reeli oynat"
                  >
                    <video
                      src={reel.videoUrl}
                      muted
                      preload="metadata"
                      playsInline
                      style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }}
                    />
                  </button>
                  <div className="card-body d-flex justify-content-between align-items-center">
                    <small className="text-muted">Kaydedildi</small>
                    <Button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => void handleUnsave(reel)}
                      disabled={saveLoading}
                    >
                      <i className="fas fa-bookmark me-1" />
                      Kaldır
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />

      {activeReel && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setActiveReelId(null);
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content bg-transparent border-0">
              <div className="modal-header border-0">
                <button
                  type="button"
                  className="btn-close btn-close-white ms-auto"
                  aria-label="Kapat"
                  onClick={() => setActiveReelId(null)}
                />
              </div>
              <div className="modal-body p-0 d-flex justify-content-center">
                <div
                  style={{
                    width: 'min(90vw, 420px)',
                    height: '70vh',
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    background: '#000',
                  }}
                >
                  <ReelPlayer videoUrl={activeReel.videoUrl} isActive playWithAudio />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KaydedilenGonderiler;
